import { useEffect, useRef } from 'react'
import { presenceApi } from '@api/endpoints/presence'
import { PresenceState } from '@api/types/presence'
import { usePresenceStore } from '@store/presenceStore'

/**
 * Tell the server this browser is alive, and collect presence for whatever is on screen.
 *
 * Mounted once, app-wide. One request per beat serves every component, because the watch list
 * comes from the store rather than from whoever happens to be rendering.
 *
 * A hidden tab drops to a slow beat rather than stopping: it is genuinely "away" — the person is
 * still at the machine — and going silent would flip them to Offline for their friends within
 * the staleness window.
 *
 * Note it keeps beating even when the user has chosen to appear offline. Suppression happens on
 * the server; stopping the beat here would put an enforcement decision in the client, and would
 * make becoming visible again take a full staleness window instead of one beat.
 */

/**
 * How often to beat, by what the page is actually doing.
 *
 * A status change only becomes visible on the viewer's next beat, so the interval *is* the
 * perceived latency. At a flat 30s a friend switching to Busy took up to half a minute to show,
 * which reads as broken even when it is working.
 *
 * The faster rate applies only while something is on screen that wants presence — a chat header,
 * a member list. Watching nobody costs the server the same query either way, so there is no point
 * paying for it. The server caches the friend set for 60s, which is what makes the faster rate
 * roughly free.
 *
 * Mobile deliberately stays at 30s: it only beats while the app is open, and the battery is a
 * scarcer resource than a desktop's mains power.
 */
const WATCHING_MS = 15_000
const IDLE_MS = 30_000
const HIDDEN_MS = 120_000

/**
 * The interval re-evaluates rather than reschedules.
 *
 * The desired period changes when the watch list does — opening a conversation should speed the
 * beat up — and chasing that with clearInterval/setInterval every time a component mounts would
 * be easy to get wrong. A cheap tick that decides whether enough time has passed costs nothing:
 * it makes no request.
 */
const TICK_MS = 5_000

export function usePresenceHeartbeat(enabled: boolean): void {
  const timer = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (!enabled) return

    let cancelled = false

    let lastBeatAt = 0

    const desiredPeriod = (): number => {
      if (document.visibilityState !== 'visible') return HIDDEN_MS
      return usePresenceStore.getState().watchedIds().length > 0 ? WATCHING_MS : IDLE_MS
    }

    const beat = async () => {
      if (cancelled) return
      lastBeatAt = Date.now()
      const activity =
        document.visibilityState === 'visible' ? PresenceState.Online : PresenceState.Away
      try {
        const res = await presenceApi.heartbeat(
          activity,
          usePresenceStore.getState().watchedIds()
        )
        if (!cancelled) usePresenceStore.getState().apply(res.users)
      } catch {
        // Non-fatal by design. A missed beat costs at most one interval of freshness, and a
        // network blip should not clear anybody's dot.
      }
    }

    const schedule = () => {
      if (timer.current) clearInterval(timer.current)
      timer.current = setInterval(() => {
        if (Date.now() - lastBeatAt >= desiredPeriod()) void beat()
      }, TICK_MS)
    }

    const onVisibility = () => {
      // Beat immediately on the change so coming back to the tab is instant rather than waiting
      // out the remainder of an interval.
      void beat()
      schedule()
    }

    // `pagehide` rather than `beforeunload`: it fires for the back/forward cache and on mobile
    // Safari, where `beforeunload` frequently does not.
    const onPageHide = () => presenceApi.signOffOnUnload()

    void beat()
    schedule()
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', onPageHide)

    return () => {
      cancelled = true
      if (timer.current) clearInterval(timer.current)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', onPageHide)
    }
  }, [enabled])

  // Signing out should not leave you looking online to your friends for a minute and a half.
  useEffect(() => {
    if (enabled) return
    usePresenceStore.getState().clear()
  }, [enabled])
}
